"use client";

import { useEffect, useState } from "react";
import {
    getWorkspaceImageCachedSrc,
    preloadWorkspaceImageSrc,
} from "@/lib/workspaceImageCache";

type WorkspaceImageProps = {
    src: string;
    alt: string;
    className?: string;
    onLoad?: () => void;
    onError?: () => void;
};

export function WorkspaceImage({
    src,
    alt,
    className = "",
    onLoad,
    onError,
}: WorkspaceImageProps) {
    const [resolvedSrc, setResolvedSrc] = useState<string | null>(() =>
        getWorkspaceImageCachedSrc(src),
    );

    useEffect(() => {
        if (!src.trim()) {
            setResolvedSrc(null);
            onError?.();
            return;
        }

        const cached = getWorkspaceImageCachedSrc(src);
        if (cached) {
            setResolvedSrc(cached);
            onLoad?.();
            return;
        }

        let cancelled = false;

        void preloadWorkspaceImageSrc(src).then((nextSrc) => {
            if (cancelled) return;
            if (!nextSrc) {
                setResolvedSrc(null);
                onError?.();
                return;
            }
            setResolvedSrc(nextSrc);
            onLoad?.();
        });

        return () => {
            cancelled = true;
        };
    }, [src, onLoad, onError]);

    if (!resolvedSrc) return null;

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={resolvedSrc}
            alt={alt}
            className={className}
            draggable={false}
            onError={() => onError?.()}
        />
    );
}
